import { useEffect } from "react";

const INTERACTIVE_TARGET_SELECTOR = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "[contenteditable='true']",
  "[role='button']",
  "[role='combobox']",
  "[role='textbox']",
  "[data-review-grid-interactive='true']",
].join(",");

const isInteractiveTarget = (target) =>
  Boolean(target?.closest?.(INTERACTIVE_TARGET_SELECTOR));

const isBlockedReviewGridKeyEvent = (event) =>
  event.defaultPrevented ||
  event.repeat ||
  event.isComposing ||
  event.altKey ||
  event.ctrlKey ||
  event.metaKey ||
  event.shiftKey ||
  isInteractiveTarget(event.target);

const REVIEW_EDITOR_SELECTOR = "[data-expense-review-editor='true']";
const DIRECT_TEXT_ENTRY_BINDINGS = new Set([
  "approvedAmount",
  "statusComments",
]);

const getOrderedReviewEditors = (grid) => {
  const hostElement = grid?.hostElement;
  if (!hostElement) return [];

  return Array.from(hostElement.querySelectorAll(REVIEW_EDITOR_SELECTOR))
    .filter((editor) => !editor.disabled && editor.offsetParent !== null)
    .sort(
      (firstEditor, secondEditor) =>
        Number(firstEditor.dataset.expenseReviewEditorOrder) -
        Number(secondEditor.dataset.expenseReviewEditorOrder),
    );
};

const getEditorColumnBinding = (editorType) =>
  ({
    approvedAmount: "approvedAmount",
    expStatus: "expStatusAbbr",
    statusComments: "statusComments",
  })[editorType];

const focusReviewEditor = (grid, editor) => {
  if (!editor) return false;

  const editorOrder = Number(editor.dataset.expenseReviewEditorOrder);
  const columnBinding = getEditorColumnBinding(
    editor.dataset.expenseReviewEditorType,
  );

  if (Number.isFinite(editorOrder) && columnBinding) {
    selectReviewGridCell(grid, Math.floor(editorOrder / 10), columnBinding);
  }

  editor.focus?.();
  if (editor.dataset.expenseReviewEditorType === "approvedAmount") {
    editor.select?.();
  }

  return true;
};

export const focusReviewEditorAtOrder = (grid, editorOrder) =>
  focusReviewEditor(
    grid,
    getOrderedReviewEditors(grid).find(
      (editor) =>
        Number(editor.dataset.expenseReviewEditorOrder) === editorOrder,
    ),
  );

export const focusReviewEditorAfterOrder = (grid, editorOrder) =>
  focusReviewEditor(
    grid,
    getOrderedReviewEditors(grid).find(
      (editor) => Number(editor.dataset.expenseReviewEditorOrder) > editorOrder,
    ),
  );

export const focusReviewGridRow = (grid, rowIndex) => {
  const lastRowIndex = (grid?.rows?.length ?? 0) - 1;
  if (lastRowIndex < 0) return;

  const safeRowIndex = Math.min(Math.max(rowIndex, 0), lastRowIndex);
  const currentColumnIndex = Math.max(grid?.selection?.col ?? 0, 0);

  grid.select(safeRowIndex, currentColumnIndex);
  grid.focus();
};

export const selectReviewGridCell = (grid, rowIndex, columnBinding) => {
  const lastRowIndex = (grid?.rows?.length ?? 0) - 1;
  if (lastRowIndex < 0) return false;

  const column = grid?.columns?.getColumn?.(columnBinding);
  const columnIndex =
    column?.index ??
    Array.from(grid?.columns ?? []).findIndex(
      (gridColumn) => gridColumn?.binding === columnBinding,
    );

  if (columnIndex < 0) return false;

  const safeRowIndex = Math.min(Math.max(rowIndex, 0), lastRowIndex);
  grid.select(safeRowIndex, columnIndex);
  return true;
};

export const focusReviewGridCell = (grid, rowIndex, columnBinding) => {
  if (!selectReviewGridCell(grid, rowIndex, columnBinding)) return false;

  grid.focus();
  return true;
};

export const focusReviewGridOnInitialization = ({
  grid,
  hasFocusedRef,
  schedule = (callback) => window.requestAnimationFrame(callback),
}) => {
  if (!grid || !hasFocusedRef || hasFocusedRef.current) return false;

  hasFocusedRef.current = true;
  schedule(() => grid.focus?.());
  return true;
};

export const focusNextReviewRow = (grid, currentRowIndex) =>
  focusReviewGridRow(grid, currentRowIndex + 1);

export const getNextReviewRowIndex = (rowIndex, rowCount) => {
  if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= rowCount - 1) {
    return null;
  }

  return rowIndex + 1;
};

export const applyReviewGridEnter = ({
  enabled,
  event,
  grid,
  onEnterSelectedCell,
}) => {
  if (
    !enabled ||
    event.key !== "Enter" ||
    isBlockedReviewGridKeyEvent(event) ||
    !onEnterSelectedCell
  ) {
    return false;
  }

  const rowIndex = grid.selection?.row ?? -1;
  const columnIndex = grid.selection?.col ?? -1;
  const row = rowIndex >= 0 ? grid.rows?.[rowIndex]?.dataItem : null;
  const columnBinding =
    columnIndex >= 0 ? grid.columns?.[columnIndex]?.binding : null;

  if (
    !row ||
    onEnterSelectedCell({ columnBinding, grid, row, rowIndex }) !== true
  ) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();
  return true;
};

export const applyReviewGridFunctionKey = ({
  enabled,
  event,
  grid,
  onOpenSelectedRow,
}) => {
  if (
    !enabled ||
    event.key !== "F3" ||
    event.defaultPrevented ||
    event.repeat ||
    event.isComposing ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    !onOpenSelectedRow
  ) {
    return false;
  }

  const rowIndex = grid.selection?.row ?? -1;
  const row = rowIndex >= 0 ? grid.rows?.[rowIndex]?.dataItem : null;
  const commentsEditor = getOrderedReviewEditors(grid).find(
    (editor) =>
      Math.floor(Number(editor.dataset.expenseReviewEditorOrder) / 10) ===
        rowIndex &&
      editor.dataset.expenseReviewEditorType === "statusComments",
  );

  if (
    !row ||
    onOpenSelectedRow(row, rowIndex, commentsEditor?.value) !== true
  ) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();
  return true;
};

export const applyReviewGridTextEntry = ({ enabled, event, grid }) => {
  if (
    !enabled ||
    event.defaultPrevented ||
    event.repeat ||
    event.isComposing ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.key.length !== 1 ||
    isInteractiveTarget(event.target)
  ) {
    return false;
  }

  const rowIndex = grid.selection?.row ?? -1;
  const columnIndex = grid.selection?.col ?? -1;
  const columnBinding =
    columnIndex >= 0 ? grid.columns?.[columnIndex]?.binding : null;

  if (!DIRECT_TEXT_ENTRY_BINDINGS.has(columnBinding) || rowIndex < 0) {
    return false;
  }

  const editor = getOrderedReviewEditors(grid).find(
    (candidate) =>
      Math.floor(Number(candidate.dataset.expenseReviewEditorOrder) / 10) ===
        rowIndex &&
      getEditorColumnBinding(candidate.dataset.expenseReviewEditorType) ===
        columnBinding,
  );

  return focusReviewEditor(grid, editor);
};

export const applyReviewGridShortcut = ({
  enabled,
  event,
  getStatusByShortcut,
  grid,
  onApplyStatus,
  schedule = (callback) => window.requestAnimationFrame(callback),
}) => {
  if (
    !enabled ||
    isBlockedReviewGridKeyEvent(event)
  ) {
    return false;
  }

  const statusId = getStatusByShortcut(event.key);
  if (statusId === null) return false;

  const rowIndex = grid.selection?.row ?? -1;
  const columnIndex = grid.selection?.col ?? -1;
  const row = rowIndex >= 0 ? grid.rows?.[rowIndex]?.dataItem : null;
  const columnBinding =
    columnIndex >= 0 ? grid.columns?.[columnIndex]?.binding : null;

  if (columnBinding !== "expStatusAbbr") return false;
  if (!row) return false;

  event.preventDefault();
  event.stopPropagation();

  const shouldFocusNextRow = onApplyStatus(row, statusId, rowIndex) !== false;
  if (shouldFocusNextRow) {
    schedule(() => focusNextReviewRow(grid, rowIndex));
  }

  return true;
};

const useReviewGridShortcuts = ({
  enabled,
  getStatusByShortcut,
  gridRef,
  onApplyStatus,
  onEnterSelectedCell,
  onOpenSelectedRow,
}) => {
  useEffect(() => {
    const grid = gridRef.current;
    const hostElement = grid?.hostElement;

    if (!hostElement) return undefined;

    hostElement.classList.toggle("expense-review-hotkeys-enabled", enabled);

    const handleClick = (event) => {
      if (!enabled || isInteractiveTarget(event.target)) return;

      grid.focus();
    };

    const handleKeyDown = (event) => {
      if (
        applyReviewGridFunctionKey({
          enabled,
          event,
          grid,
          onOpenSelectedRow,
        })
      ) {
        return;
      }

      if (
        applyReviewGridEnter({
          enabled,
          event,
          grid,
          onEnterSelectedCell,
        })
      ) {
        return;
      }

      if (
        applyReviewGridShortcut({
          enabled,
          event,
          getStatusByShortcut,
          grid,
          onApplyStatus,
        })
      ) {
        return;
      }

      applyReviewGridTextEntry({
        enabled,
        event,
        grid,
      });
    };

    hostElement.addEventListener("click", handleClick);
    hostElement.addEventListener("keydown", handleKeyDown, true);

    return () => {
      hostElement.classList.remove("expense-review-hotkeys-enabled");
      hostElement.removeEventListener("click", handleClick);
      hostElement.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    enabled,
    getStatusByShortcut,
    gridRef,
    onApplyStatus,
    onEnterSelectedCell,
    onOpenSelectedRow,
  ]);
};

export default useReviewGridShortcuts;
